import { inject, Injectable } from '@angular/core';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  UploadException,
} from '@web/shared/ui';
import { catchError, combineLatest, map, Observable, throwError } from 'rxjs';
import { AdminBrandCollectionFilter } from '../models/admin-brand-collection-filter';
import { AdminBrandCollectionItem } from '../models/admin-brand-collection-item';
import { blank, Guid, once } from '@web/utils';
import { AdminBrandDetailsForm } from '../models/admin-brand-details-form';
import {
  AdminBrandApiService,
  AdminBrandCreateRequest,
  AdminBrandCreateSocialMediaRequest,
  AdminBrandUpdateRequest,
  BrandSocialMedia,
} from '@web/shared/module/brand';
import { AdminMultimediaDataSourceService } from '@web/shared/module/multimedia';
import { SummernoteImageUpload } from '@web/shared/external';
import { CollectionRequestBuilder, HttpErrorService } from '@web/common';
import { AdminBrandDetails } from '../models/admin-brand-details';
import { AdminBrandDetailsFormSocialMediaCollection } from '../models/admin-brand-details-form-social-media-collection';
import { AdminBrandDetailsFormSocialMedia } from '../models/admin-brand-details-form-social-media';

@Injectable({
  providedIn: 'root',
})
export class AdminBrandDataSourceService {
  private readonly adminBrandApi = inject(AdminBrandApiService);
  private readonly confirmation = inject(ConfirmationService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly httpError = inject(HttpErrorService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminBrandCollectionFilter>,
  ): Observable<TableDataPayload<AdminBrandCollectionItem>> {
    return this.adminBrandApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('name').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('slug').build(),
              )
              .appendText((builder) => builder.equalFor('status').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminBrandCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminBrandCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeEdited,
                    createdAt: item.createdAt,
                    homeUrl: item.homeUrl,
                    id: item.id,
                    logoAlt: item.logo?.alt(),
                    logoUrl: item.logo?.url,
                    name: item.name,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsFormById(id: Guid): Observable<AdminBrandDetailsForm> {
    return this.adminBrandApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminBrandDetailsForm({
            name: response.name,
            description: response.description,
            logoId: response.logo?.id,
            illustrationId: response.illustration?.id,
            status: response.status,
            homeUrl: response.homeUrl,
            socialMedia: new AdminBrandDetailsFormSocialMediaCollection(
              response.socialMedia.map(
                (item) =>
                  new AdminBrandDetailsFormSocialMedia(item.id, item.url),
              ),
            ),
            slug: response.slug,
          }),
      ),
    );
  }

  public createByFormData(data: AdminBrandDetailsForm): Observable<void> {
    return this.adminBrandApi
      .create(
        new AdminBrandCreateRequest({
          name: data.name,
          description: data.description,
          logoFileId: data.logoId,
          illustrationFileId: data.illustrationId,
          status: data.status,
          homeUrl: data.homeUrl,
          socialNetworks: data.socialMedia?.items.map(
            (item) => new AdminBrandCreateSocialMediaRequest(item.id, item.url),
          ),
        }),
      )
      .pipe(blank());
  }

  public updateByFormData(
    id: Guid,
    data: AdminBrandDetailsForm,
  ): Observable<void> {
    return this.adminBrandApi.update(
      id,
      new AdminBrandUpdateRequest({
        name: data.name,
        description: data.description,
        logoFileId: data.logoId,
        illustrationFileId: data.illustrationId,
        status: data.status,
        homeUrl: data.homeUrl,
        slug: data.slug,
        socialNetworks: data.socialMedia?.items.map(
          (item) => new AdminBrandCreateSocialMediaRequest(item.id, item.url),
        ),
      }),
    );
  }

  public uploadEditorImages(
    files: File[],
  ): Observable<SummernoteImageUpload[]> {
    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource
          .upload(file)
          .pipe(
            catchError((response) =>
              throwError(
                () =>
                  new UploadException(
                    this.httpError.extract(response).multiLineMessages(),
                  ),
              ),
            ),
          ),
      ),
    ).pipe(
      map((items) =>
        items.map(
          (item) => new SummernoteImageUpload(item.previewUrl, item.filename),
        ),
      ),
    );
  }

  public removeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybraną markę kosmetyczną?')
          .accept(() =>
            this.adminBrandApi.remove(id).pipe(once()).subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public fetchDetailsById(id: Guid): Observable<AdminBrandDetails> {
    return this.adminBrandApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new AdminBrandDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            description: response.description,
            homeUrl: response.homeUrl,
            id: response.id,
            illustrationAlt: response.illustration?.alt(),
            illustrationSrc: response.illustration?.url,
            name: response.name,
            socials: response.socialMedia.map((item) => new BrandSocialMedia(item.id, item.code, item.url)),
            status: response.status,
          }),
      ),
    );
  }
}
